import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ShieldAlert } from "lucide-react";
import { useAlerts } from "@/hooks/useAlerts";
import { joinWithAnd } from "@/utils/helperFunctions";
import { selectSharingOffWordAlerts } from "../sharingOffAlerts";

dayjs.extend(relativeTime);

// Bold and underline the flagged words, as WordAlertCard does.
function highlightWords(text, words) {
  const cleaned = words.filter(Boolean);
  if (!cleaned.length) return text;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`\\b(${cleaned.map(escape).join("|")})\\b`, "gi");
  return text.split(pattern).map((part, index) =>
    cleaned.some((w) => w.toLowerCase() === part.toLowerCase()) ? (
      <span key={index} className="font-bold underline">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

// Word alerts from students who have data sharing off. Shown on their own so a
// safety alert is never hidden by the toggle, while the student's wellness
// data stays private: there is deliberately no way to open the student from
// here. See sharingOffAlerts.js.
const SharingOffSafetyAlerts = ({ alertGroups, highlightAlertId }) => {
  const items = selectSharingOffWordAlerts(alertGroups);
  const { markAlertAsRead, markAlertAsUnread } = useAlerts();
  const [pendingId, setPendingId] = useState(null);
  const highlightRef = useRef(null);

  // Arriving from an alert email: bring that alert into view.
  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [highlightAlertId, items.length]);

  if (items.length === 0) return null;

  const toggleRead = async (item) => {
    setPendingId(item.alertId);
    try {
      if (item.read) {
        await markAlertAsUnread(item.alertId);
      } else {
        await markAlertAsRead(item.alertId);
      }
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section
      className="bg-white rounded border border-[#DDDDDD] shadow-sm"
      aria-label="Safety alerts from students not sharing data"
    >
      <div className="flex items-start gap-3 p-3 border-b border-[#DDDDDD]">
        <ShieldAlert className="w-5 h-5 shrink-0 text-[#C53030] mt-0.5" />
        <div>
          <h2 className="text-[16px] font-semibold">
            Safety alerts: students not sharing data
          </h2>
          <p className="text-[13px] text-gray-600">
            These students have data sharing turned off, so only their word
            alerts, and what they wrote that triggered them, are shown here.
            The rest of their wellness data stays private.
          </p>
        </div>
      </div>

      <ul>
        {items.map((item) => {
          const isHighlighted =
            highlightAlertId && item.alertId === String(highlightAlertId);
          return (
            <li
              key={item.alertId}
              ref={isHighlighted ? highlightRef : undefined}
              className={`flex items-start justify-between gap-3 px-3 py-2 border-t border-[#EEEEEE] first:border-t-0 ${
                item.read ? "bg-white" : "bg-red-50"
              } ${isHighlighted ? "ring-2 ring-[#1879EE] ring-inset" : ""}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold truncate">
                    {item.studentName}
                  </span>
                  <span className="text-[12px] text-gray-400 shrink-0">
                    {dayjs(item.createdAt).fromNow()}
                  </span>
                </div>
                <div className="text-[14px] opacity-80">
                  <span className="font-semibold">Word Alert:</span> Used the
                  word{item.words.length > 1 ? "s" : ""} "
                  {joinWithAnd(item.words)}"
                </div>
                {item.message && (
                  <p className="text-[14px] mt-1 text-gray-800 break-words">
                    <span className="font-semibold">What they wrote:</span> "
                    {highlightWords(item.message, item.words)}"
                  </p>
                )}
              </div>
              <button
                type="button"
                className="text-[13px] text-[#1A6BE5] hover:text-[#1a4d7a] whitespace-nowrap disabled:opacity-50"
                onClick={() => toggleRead(item)}
                disabled={pendingId === item.alertId}
              >
                {item.read ? "Mark as Unread" : "Mark as Read"}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SharingOffSafetyAlerts;
